import { Link, useSearchParams } from 'react-router';
import type { ProjectStatus } from '@mys/contracts';
import { ApiError } from '../lib/api.js';
import {
  daysLate,
  nextProjectStatus,
  PROJECT_STATUSES,
  PROJECT_STATUS_LABELS,
  useProjects,
  useUpdateProject,
} from '../lib/projects.js';
import { formatDate, formatInr } from '../lib/format.js';

/* ============================================================================
   /projects — what is being built, and what is late.
   ----------------------------------------------------------------------------
   A list rather than a kanban, unlike leads. A lead board answers "how many
   are at each stage"; this one answers "what needs attention today", and the
   answer is ordered by how late something is, which a set of columns cannot
   show. The API already sorts oldest promise first.
   ========================================================================== */

export function ProjectsPage() {
  const [params, setParams] = useSearchParams();

  const statusParam = params.get('status');
  const status = PROJECT_STATUSES.includes(statusParam as ProjectStatus)
    ? (statusParam as ProjectStatus)
    : undefined;
  const overdueOnly = params.get('overdue') === 'true';

  const { data, isPending, error } = useProjects({
    ...(status ? { status } : {}),
    ...(overdueOnly ? { overdue: true } : {}),
  });

  function setParam(key: string, value: string | undefined) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  }

  const items = data?.items ?? [];
  const counts = data?.counts ?? {};

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Projects</h1>
          <p className="muted">
            {data ? `${data.total} project` : 'Load ho raha hai…'}
            {data && data.overdue > 0 ? ` · ${data.overdue} late` : ''}
          </p>
        </div>
      </div>

      <div className="row" style={{ marginBottom: 16 }}>
        <button
          type="button"
          className={status ? 'btn btn--ghost btn--small' : 'btn btn--small'}
          onClick={() => setParam('status', undefined)}
        >
          All
        </button>
        {PROJECT_STATUSES.map((value) => (
          <button
            key={value}
            type="button"
            className={status === value ? 'btn btn--small' : 'btn btn--ghost btn--small'}
            onClick={() => setParam('status', value)}
          >
            {PROJECT_STATUS_LABELS[value]} {counts[value] ?? 0}
          </button>
        ))}

        {/* Separate from the status filter because it cuts across all of them:
            a project can be late in any stage except live. */}
        <button
          type="button"
          className={overdueOnly ? 'btn btn--small' : 'btn btn--ghost btn--small'}
          onClick={() => setParam('overdue', overdueOnly ? undefined : 'true')}
        >
          Late only {data?.overdue ?? 0}
        </button>
      </div>

      {error ? (
        <p className="alert" role="alert">
          {error instanceof ApiError ? error.message : 'Could not load projects.'}
        </p>
      ) : null}

      {isPending ? <p className="muted">Loading…</p> : null}

      {data && items.length === 0 ? (
        <div className="card">
          <p style={{ margin: 0 }}>
            {overdueOnly || status ? 'Nothing matches this filter.' : 'No projects yet.'}
          </p>
          {!overdueOnly && !status ? (
            <>
              <p className="muted" style={{ margin: '6px 0 0' }}>
                A project is created from inside a client.
              </p>
              <Link to="/clients" className="btn btn--small" style={{ marginTop: 12 }}>
                Open clients
              </Link>
            </>
          ) : null}
        </div>
      ) : null}

      <div className="stack">
        {items.map((project) => {
          const late = daysLate(project);
          return (
            <article className="card" key={project.id}>
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <div>
                  <Link to={`/projects/${project.id}`} className="lead-card__business">
                    {project.business}
                  </Link>
                  <div className="lead-card__meta">
                    {project.plan} · {formatInr(project.priceInr)}
                  </div>
                </div>
                <div className="row" style={{ gap: 6 }}>
                  {late !== null ? (
                    <span className="pill pill--warn">
                      {late} days late
                      <span className="sr-only"> — past its promised date</span>
                    </span>
                  ) : null}
                  <span className="pill pill--muted">{PROJECT_STATUS_LABELS[project.status]}</span>
                </div>
              </div>

              <div className="lead-card__meta" style={{ marginTop: 8 }}>
                {project.promisedDate
                  ? `Promised ${formatDate(project.promisedDate)}`
                  : 'No date promised'}
                {project.launchedAt ? ` · live ${formatDate(project.launchedAt)}` : ''}
              </div>

              <AdvanceButton project={project} />
            </article>
          );
        })}
      </div>
    </>
  );
}

/** One tap to the next stage, like the lead board's. */
function AdvanceButton({
  project,
}: {
  project: { id: string; status: ProjectStatus; clientId: string };
}) {
  const update = useUpdateProject(project.id);
  const next = nextProjectStatus(project.status);
  if (!next) return null;

  return (
    <div className="row" style={{ marginTop: 8, gap: 6 }}>
      <button
        type="button"
        className="btn btn--small"
        disabled={update.isPending}
        onClick={() => update.mutate({ status: next })}
      >
        → {PROJECT_STATUS_LABELS[next]}
      </button>
      {update.isError ? (
        <span className="field__error" role="alert">
          {update.error instanceof ApiError ? update.error.message : 'Could not save.'}
        </span>
      ) : null}
    </div>
  );
}
